import { motion } from "framer-motion";
import {
  FaRupeeSign,
  FaCoins,
  FaWallet,
  FaShieldAlt,
  FaChartLine,
  FaHandHoldingUsd,
} from "react-icons/fa";
import "./Hero.css";

const floatIcons = [
  { Icon: FaRupeeSign, top: "10%", left: "4%", delay: 0, size: 22 },
  { Icon: FaCoins, top: "68%", left: "2%", delay: 0.4, size: 26 },
  { Icon: FaWallet, top: "18%", left: "88%", delay: 0.8, size: 24 },
  { Icon: FaShieldAlt, top: "78%", left: "84%", delay: 1.1, size: 22 },
  { Icon: FaChartLine, top: "48%", left: "94%", delay: 0.6, size: 20 },
  { Icon: FaHandHoldingUsd, top: "4%", left: "56%", delay: 1.4, size: 20 },
];

const heroTextVariants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.15 * i, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="lf-hero">
      <div className="lf-hero__bg">
        <span className="lf-hero__blob lf-hero__blob--one" />
        <span className="lf-hero__blob lf-hero__blob--two" />
        <span className="lf-hero__blob lf-hero__blob--three" />
        <div className="lf-hero__grid-lines" />
      </div>

      {floatIcons.map(({ Icon, top, left, delay, size }, i) => (
        <motion.span
          key={i}
          className="lf-hero__float-icon"
          style={{ top, left, fontSize: size }}
          animate={{ y: [0, -16, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay }}
        >
          <Icon />
        </motion.span>
      ))}

      <div className="container lf-hero__inner">
        <div className="lf-hero__left">
          <motion.span custom={0} variants={heroTextVariants} initial="hidden" animate="visible" className="eyebrow">
            <FaShieldAlt /> KML NO : 32090439159
          </motion.span>

          <motion.h1 custom={1} variants={heroTextVariants} initial="hidden" animate="visible" className="lf-hero__title">
            LENT <span className="accent-text">FINANCE</span>
          </motion.h1>

          <motion.div custom={2} variants={heroTextVariants} initial="hidden" animate="visible" className="lf-hero__tags">
            <span>Trusted Gold Loans</span>
            <span>Quick Personal Loans</span>
            <span>Business Loans</span>
            <span>Easy Financial Solutions</span>
          </motion.div>

          <motion.p custom={3} variants={heroTextVariants} initial="hidden" animate="visible" className="lf-hero__desc">
            Transparent policies, fast approvals, and financial guidance you can rely on —
            built on years of trusted service to families and businesses across Kerala.
          </motion.p>

          <motion.div custom={4} variants={heroTextVariants} initial="hidden" animate="visible" className="lf-hero__cta-row">
            <button className="btn btn-gold" onClick={() => scrollTo("contact")}>
              Apply Now
            </button>
            <button className="btn btn-outline" onClick={() => scrollTo("contact")}>
              Contact Us
            </button>
          </motion.div>

          <motion.div custom={5} variants={heroTextVariants} initial="hidden" animate="visible" className="lf-hero__trust-row">
            <div>
              <strong>10+</strong>
              <span>Years Experience</span>
            </div>
            <div className="lf-hero__trust-divider" />
            <div>
              <strong>5000+</strong>
              <span>Happy Customers</span>
            </div>
            <div className="lf-hero__trust-divider" />
            <div>
              <strong>100%</strong>
              <span>Transparent Service</span>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="lf-hero__right"
          initial={{ opacity: 0, scale: 0.9, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.3 }}
        >
          <HeroIllustration />
        </motion.div>
      </div>
    </section>
  );
};

const HeroIllustration = () => (
  <div className="lf-hero__illustration">
    <motion.div
      className="lf-hero__coin-ring"
      animate={{ rotate: 360 }}
      transition={{ duration: 26, repeat: Infinity, ease: "linear" }}
    />
    <svg viewBox="0 0 480 480" className="lf-hero__svg" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cardGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0B4F8A" />
          <stop offset="100%" stopColor="#16A6D9" />
        </linearGradient>
        <linearGradient id="goldGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#F0CF7C" />
          <stop offset="100%" stopColor="#C9982E" />
        </linearGradient>
      </defs>

      {/* base platform */}
      <ellipse cx="240" cy="410" rx="170" ry="18" fill="#0B4F8A" opacity="0.08" />

      {/* consultant desk / approval card */}
      <rect x="120" y="150" width="220" height="150" rx="18" fill="#FFFFFF" stroke="#E4EEF6" strokeWidth="2" />
      <rect x="140" y="176" width="130" height="12" rx="6" fill="url(#cardGrad)" />
      <rect x="140" y="200" width="180" height="8" rx="4" fill="#DCEAF3" />
      <rect x="140" y="216" width="150" height="8" rx="4" fill="#DCEAF3" />
      <circle cx="290" cy="255" r="26" fill="none" stroke="url(#goldGrad)" strokeWidth="6" />
      <path d="M278 255 L287 264 L304 244" fill="none" stroke="#C9982E" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round" />

      {/* family silhouette */}
      <g transform="translate(60,255)">
        <circle cx="20" cy="10" r="14" fill="#0B4F8A" />
        <path d="M0 70 C0 40 40 40 40 70 L40 90 L0 90 Z" fill="#0B4F8A" />
        <circle cx="52" cy="18" r="11" fill="#16A6D9" />
        <path d="M36 70 C36 48 68 48 68 70 L68 90 L36 90 Z" fill="#16A6D9" />
        <circle cx="10" cy="40" r="7" fill="#32C8FF" />
        <path d="M0 78 C0 64 20 64 20 78 L20 90 L0 90 Z" fill="#32C8FF" />
      </g>

      {/* gold coin stack */}
      <g transform="translate(320,300)">
        <ellipse cx="20" cy="46" rx="26" ry="9" fill="url(#goldGrad)" />
        <ellipse cx="20" cy="34" rx="26" ry="9" fill="url(#goldGrad)" />
        <ellipse cx="20" cy="22" rx="26" ry="9" fill="url(#goldGrad)" />
        <ellipse cx="20" cy="10" rx="26" ry="9" fill="#F0CF7C" />
        <text x="20" y="15" textAnchor="middle" fontSize="11" fill="#8F6C17" fontFamily="Poppins" fontWeight="700">
          ₹
        </text>
      </g>

      {/* graph line */}
      <polyline points="130,320 165,300 195,330 230,295 260,315" fill="none" stroke="#16A6D9" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />
      <circle cx="260" cy="315" r="5" fill="#0B4F8A" />
    </svg>
  </div>
);

export default Hero;
